import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './GasStationMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const normalizeBrand = (brand = '') => {
  return brand
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

const LOGO_BASE_PATH = `${process.env.PUBLIC_URL || ''}/logos`;

const getBrandLogoPath = (brand) => {
  const logoFiles = {
    circlek: 'circlek.png',
    gmcirclek: 'circlek.png',
    plungeslagunacirclek: 'circlek.png',
    vvarffcirclek: 'circlek.png',
    viada: 'viada.png',
    orlen: 'orlen.png',
    neste: 'neste.webp',
    nestelietuva: 'neste.webp',
    alausa: 'alausa.jpg',
    emsi: 'emsi.png',
    stateta: 'stateta.png',
    naftrus: 'naftrus.jpg',
    apsaga: 'apsaga.png',
    lukoil: 'lukoil.png',
    gotika: 'gotika.png',
    expres: 'expres.png',
    statoil: 'statoil.png',
    gulf: 'gulf.png',
    tauras: 'tauras.png',
    deglita: 'deglita.png',
    virsi: 'virsi.png',
    saurida: 'saurida.png',
    trevena: 'trevena.png',
    as24lietuva: 'as24.png',
    as24: 'as24.png',
    bpetroleum: 'bpetroleum.png',
    balticpetroleum: 'bpetroleum.png',
    jozita: 'jozita.jfif',
  };

  const normalized = normalizeBrand(brand);

  if (!normalized) return null;

  const directMatch = logoFiles[normalized];
  if (directMatch) return `${LOGO_BASE_PATH}/${directMatch}`;

  if (normalized.includes('circlek')) return `${LOGO_BASE_PATH}/circlek.png`;
  if (normalized.includes('orlen')) return `${LOGO_BASE_PATH}/orlen.png`;
  if (normalized.includes('viada')) return `${LOGO_BASE_PATH}/viada.png`;
  if (normalized.includes('neste')) return `${LOGO_BASE_PATH}/neste.webp`;
  if (normalized.includes('emsi')) return `${LOGO_BASE_PATH}/emsi.png`;
  if (normalized.includes('stateta')) return `${LOGO_BASE_PATH}/stateta.png`;
  if (normalized.includes('naftrus')) return `${LOGO_BASE_PATH}/naftrus.jpg`;
  if (normalized.includes('saurida')) return `${LOGO_BASE_PATH}/saurida.png`;
  if (normalized.includes('trevena')) return `${LOGO_BASE_PATH}/trevena.png`;
  if (normalized.includes('as24')) return `${LOGO_BASE_PATH}/as24.png`;
  if (normalized.includes('petroleum')) return `${LOGO_BASE_PATH}/bpetroleum.png`;
  if (normalized.includes('jozita')) return `${LOGO_BASE_PATH}/jozita.jfif`;

  return null;
};

const getBrandLogoHTML = (brand) => {
  const logoPath = getBrandLogoPath(brand);
  if (logoPath) {
    return `<img src="${logoPath}" alt="${brand}" style="width: 24px; height: 24px; object-fit: contain;" onerror="this.style.display='none'; this.nextSibling.style.display='inline';" /><span style="display:none;">⛽</span>`;
  }
  return '⛽';
};

const getGradientColor = (price, minPrice, maxPrice) => {
  if (price === 0 || !isFinite(price) || !isFinite(minPrice) || !isFinite(maxPrice)) {
    return '#9ca3af'; // Gray for no data
  }
  
  // Normalize price between 0 and 1
  const normalized = maxPrice > minPrice ? (price - minPrice) / (maxPrice - minPrice) : 0.5;
  
  // Interpolate between green (0) and red (1)
  const r = Math.round(34 + normalized * (239 - 34));   // 34 (green) to 239 (red)
  const g = Math.round(197 - normalized * (197 - 68));  // 197 (green) to 68 (red)
  const b = Math.round(94 - normalized * (94 - 68));    // 94 (green) to 68 (red)
  
  return `rgb(${r}, ${g}, ${b})`;
};

const createCustomIcon = (brand, gasPrice, dieselPrice, naturalGasPrice, selectedFuel, minPrice, maxPrice) => {
  let price = 0;
  if (selectedFuel === 'gas') {
    price = gasPrice;
  } else if (selectedFuel === 'diesel') {
    price = dieselPrice;
  } else if (selectedFuel === 'naturalGas') {
    price = naturalGasPrice;
  }
  
  const color = getGradientColor(price, minPrice, maxPrice);
  const priceText = price > 0 ? price.toFixed(2) : '—';
  const logoPath = getBrandLogoPath(brand);
  const markerLogoSize = 20;
  const markerBrandIcon = logoPath
    ? `<img src="${logoPath}" alt="${brand}" style="width: ${markerLogoSize}px; height: ${markerLogoSize}px; object-fit: contain; display: block;" onerror="this.style.display='none'; this.nextSibling.style.display='inline';" /><span style="display:none;">⛽</span>`
    : '⛽';

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${color};
        width: 58px;
        height: 44px;
        border-radius: 14px;
        border: 2px solid #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      ">
        <div style="
          color: white;
          font-weight: 800;
          font-size: 17px;
          letter-spacing: 0.2px;
          line-height: 1;
          text-shadow: 0 1px 2px rgba(0,0,0,0.25);
        ">${priceText}</div>
        <div style="
          position: absolute;
          left: 50%;
          bottom: -7px;
          transform: translateX(-50%);
          width: 12px;
          height: 12px;
          border-radius: 3px;
          background: #0f172a;
          border: 2px solid #ffffff;
          box-sizing: border-box;
        "></div>
        <div style="
          position: absolute;
          left: 50%;
          bottom: -13px;
          transform: translateX(-50%);
          background: #0f172a;
          border: 2px solid #ffffff;
          border-radius: 999px;
          padding: 1px 6px;
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
        ">
          <span style="display: inline-flex; align-items: center; justify-content: center; width: ${markerLogoSize}px; height: ${markerLogoSize}px;">${markerBrandIcon}</span>
        </div>
      </div>
    `,
    iconSize: [58, 60],
    iconAnchor: [29, 60],
    popupAnchor: [0, -46],
  });
};

function LocationMarker({ locateRequestId = 0, onLocationResult }) {
  const map = useMap();
  const userMarkerRef = useRef(null);

  useEffect(() => {
    // Request user location on mount
    map.locate({ setView: true, maxZoom: 13 });

    const onLocationFound = (e) => {
      // Remove old marker if exists
      if (userMarkerRef.current) {
        map.removeLayer(userMarkerRef.current);
      }
      
      // Add a marker for user location
      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: `
          <div style="
            width: 24px;
            height: 24px;
            background: #3b82f6;
            border: 4px solid white;
            border-radius: 50%;
            box-shadow: 0 3px 12px rgba(0,0,0,0.4);
            position: relative;
            z-index: 1000;
          ">
            <div style="
              position: absolute;
              width: 50px;
              height: 50px;
              background: rgba(59, 130, 246, 0.3);
              border: 2px solid rgba(59, 130, 246, 0.6);
              border-radius: 50%;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              animation: pulse 2s infinite;
            "></div>
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker(e.latlng, { 
        icon: userIcon,
        zIndexOffset: 1000 // Ensure it's above other markers
      })
        .addTo(map)
        .bindPopup('📍 Jūsų buvimo vieta');

      userMarkerRef.current = marker;
      
      console.log('User location found:', e.latlng);
      if (typeof onLocationResult === 'function') {
        onLocationResult({ status: 'success' });
      }
    };

    const onLocationError = (e) => {
      console.log('Location access denied or unavailable:', e.message);
      if (typeof onLocationResult === 'function') {
        onLocationResult({ status: 'error', message: e.message });
      }
    };

    map.on('locationfound', onLocationFound);
    map.on('locationerror', onLocationError);

    return () => {
      map.off('locationfound', onLocationFound);
      map.off('locationerror', onLocationError);
      if (userMarkerRef.current) {
        map.removeLayer(userMarkerRef.current);
        userMarkerRef.current = null;
      }
    };
  }, [map, onLocationResult]);

  useEffect(() => {
    if (!locateRequestId) return;
    map.locate({ setView: true, maxZoom: 13 });
  }, [map, locateRequestId]);

  return null;
}

function StationFocus({ stationFocusTarget = null }) {
  const map = useMap();
  const lastFocusKeyRef = useRef('');

  useEffect(() => {
    if (!stationFocusTarget) return;

    const hasValidCoords = Number.isFinite(stationFocusTarget.lat) && Number.isFinite(stationFocusTarget.lng);
    if (!hasValidCoords) return;

    const zoom = Number.isFinite(stationFocusTarget.zoom)
      ? stationFocusTarget.zoom
      : map.getZoom();
    const focusKey = `${stationFocusTarget.stationId || 'coords'}:${stationFocusTarget.lat}:${stationFocusTarget.lng}:${zoom}`;

    if (lastFocusKeyRef.current === focusKey) return;

    map.setView([stationFocusTarget.lat, stationFocusTarget.lng], zoom, { animate: true });
    lastFocusKeyRef.current = focusKey;
  }, [map, stationFocusTarget]);

  return null;
}

function MarkerClusterGroup({ stations, onBoundsChange, selectedFuel, minPrice, maxPrice, onStationPopupOpen, onStationDetailLinkClick }) {
  const map = useMap();

  useEffect(() => {
    const updateBounds = () => {
      const bounds = map.getBounds();
      if (onBoundsChange) {
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      }
    };

    // Initial bounds
    updateBounds();

    // Listen to map events
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map, onBoundsChange]);

  useEffect(() => {
    const markerClusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 13,  // Disable clustering when zoomed into city level
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyDistanceMultiplier: 2,  // Spread markers further apart when spiderfying
      iconCreateFunction: function(cluster) {
        const markers = cluster.getAllChildMarkers();
        const count = markers.length;
        
        // Find the best (lowest) price in this cluster for selected fuel
        let bestPrice = Infinity;
        let bestStation = null;
        markers.forEach(marker => {
          const station = stations.find(s => 
            s.lat === marker.getLatLng().lat && 
            s.lng === marker.getLatLng().lng
          );
          if (station) {
            let price = 0;
            if (selectedFuel === 'gas') price = station.gasPrice;
            else if (selectedFuel === 'diesel') price = station.dieselPrice;
            else if (selectedFuel === 'naturalGas') price = station.naturalGasPrice || 0;
            
            if (price > 0 && price < bestPrice) {
              bestPrice = price;
              bestStation = station;
            }
          }
        });
        
        // Use gradient color based on best price in cluster
        const color = getGradientColor(bestPrice, minPrice, maxPrice);
        const priceText = bestPrice < Infinity ? bestPrice.toFixed(2) : '—';
        
        // Size based on count
        let size = 48;
        if (count >= 100) size = 70;
        else if (count >= 50) size = 64;
        else if (count >= 10) size = 58;
        else if (count >= 5) size = 52;

        const clusterLogoPath = bestStation ? getBrandLogoPath(bestStation.brand) : null;
        const clusterLogoSize = Math.round(size * 0.315);
        const clusterLogoHTML = clusterLogoPath
          ? `<img src="${clusterLogoPath}" alt="${bestStation.brand}" style="width: ${clusterLogoSize}px; height: ${clusterLogoSize}px; object-fit: contain; display: block;" onerror="this.style.display='none'; this.nextSibling.style.display='inline';" /><span style="display:none;">⛽</span>`
          : '⛽';
        
        return L.divIcon({
          html: `
            <div style="
              background: ${color};
              width: ${size}px;
              height: ${size}px;
              border-radius: 50%;
              border: 3px solid #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 800;
              position: relative;
              font-size: ${Math.round(size * 0.32)}px;
              line-height: 1;
              letter-spacing: 0.3px;
              text-shadow: 0 2px 6px rgba(0,0,0,0.35);
            ">
              ${priceText}
              <div style="
                position: absolute;
                top: -6px;
                right: -4px;
                min-width: ${Math.max(18, Math.round(size * 0.32))}px;
                height: ${Math.max(18, Math.round(size * 0.32))}px;
                padding: 0 4px;
                border-radius: 999px;
                background: #ffffff;
                color: #0f172a;
                border: 2px solid #0f172a;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: ${Math.max(10, Math.round(size * 0.18))}px;
                line-height: 1;
                font-weight: 900;
              ">+${count}</div>
              <div style="
                position: absolute;
                left: 50%;
                bottom: -8px;
                transform: translateX(-50%);
                background: #0f172a;
                border: 2px solid #ffffff;
                border-radius: 999px;
                padding: 1px 6px;
                font-size: ${Math.max(10, Math.round(size * 0.19))}px;
                line-height: 1.2;
                display: inline-flex;
                align-items: center;
                white-space: nowrap;
              ">
                <span style="display: inline-flex; align-items: center; justify-content: center; width: ${clusterLogoSize}px; height: ${clusterLogoSize}px;">${clusterLogoHTML}</span>
              </div>
            </div>
          `,
          className: 'custom-cluster-icon',
          iconSize: L.point(size, size + 14),
        });
      }
    });

    // Find lowest prices
    const lowestGasPrice = Math.min(...stations.filter(s => s.gasPrice > 0).map(s => s.gasPrice));
    const lowestDieselPrice = Math.min(...stations.filter(s => s.dieselPrice > 0).map(s => s.dieselPrice));

    stations.forEach((station) => {
      const marker = L.marker([station.lat, station.lng], {
        icon: createCustomIcon(
          station.brand, 
          station.gasPrice, 
          station.dieselPrice, 
          station.naturalGasPrice || 0,
          selectedFuel,
          minPrice,
          maxPrice
        ),
      });

      const brandLogoHTML = getBrandLogoHTML(station.brand);
      const isBestGas = station.gasPrice > 0 && station.gasPrice === lowestGasPrice;
      const isBestDiesel = station.dieselPrice > 0 && station.dieselPrice === lowestDieselPrice;
      
      const popupContent = `
        <div class="popup-content">
          <div class="popup-header">
            <div class="brand-logo">${brandLogoHTML}</div>
            <h3>${station.brand}</h3>
          </div>
          <div class="popup-address">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>${station.address}, ${station.city}</span>
          </div>
          <div class="popup-prices">
            ${station.gasPrice > 0 ? `
              <div class="price-item ${isBestGas ? 'best-price' : ''}">
                <div class="price-label">
                  <span class="fuel-type">Benzinas 95:</span>
                </div>
                <span class="price">${station.gasPrice.toFixed(3)} €/L ${isBestGas ? '🏆' : ''}</span>
              </div>
            ` : ''}
            ${station.dieselPrice > 0 ? `
              <div class="price-item ${isBestDiesel ? 'best-price' : ''}">
                <div class="price-label">
                  <span class="fuel-type">Dyzelinas:</span>
                </div>
                <span class="price">${station.dieselPrice.toFixed(3)} €/L ${isBestDiesel ? '🏆' : ''}</span>
              </div>
            ` : ''}
          </div>
          <div class="popup-bottom-logo">${brandLogoHTML}</div>
          <a class="popup-detail-link" href="/degaline/${station.id}" data-station-id="${station.id}">Peržiūrėti degalinės puslapį</a>
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.on('popupopen', () => {
        if (typeof onStationPopupOpen === 'function') {
          onStationPopupOpen(station);
        }

        const popupElement = marker.getPopup()?.getElement();
        const detailLink = popupElement?.querySelector('.popup-detail-link');
        if (detailLink) {
          detailLink.addEventListener('click', () => {
            if (typeof onStationDetailLinkClick === 'function') {
              onStationDetailLinkClick(station, 'map_popup');
            }
          }, { once: true });
        }
      });
      markerClusterGroup.addLayer(marker);
    });

    map.addLayer(markerClusterGroup);

    return () => {
      map.removeLayer(markerClusterGroup);
    };
  }, [map, stations, selectedFuel, minPrice, maxPrice, onStationPopupOpen, onStationDetailLinkClick]);

  return null;
}

function GasStationMap({ stations, onBoundsChange, selectedFuel = 'gas', locateRequestId = 0, stationFocusTarget = null, onStationPopupOpen, onStationDetailLinkClick, onLocationResult }) {
  const lithuaniaBounds = [
    [53.4, 19.8],
    [57.0, 27.8]
  ];

  const center = stations.length > 0
    ? [
        stations.reduce((sum, s) => sum + s.lat, 0) / stations.length,
        stations.reduce((sum, s) => sum + s.lng, 0) / stations.length
      ]
    : [55.1694, 23.8813];

  // Calculate min/max prices for gradient based on selected fuel
  const getPriceForFuel = (station) => {
    if (selectedFuel === 'gas') return station.gasPrice;
    if (selectedFuel === 'diesel') return station.dieselPrice;
    if (selectedFuel === 'naturalGas') return station.naturalGasPrice || 0;
    return 0;
  };

  const validPrices = stations
    .map(getPriceForFuel)
    .filter(p => p > 0);
  
  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  return (
    <div className="map-container">
      <MapContainer
        center={center}
        zoom={7}
        zoomControl={false}
        minZoom={5}
        maxBounds={lithuaniaBounds}
        maxBoundsViscosity={0.55}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <StationFocus stationFocusTarget={stationFocusTarget} />
        <LocationMarker locateRequestId={locateRequestId} onLocationResult={onLocationResult} />
        <MarkerClusterGroup 
          stations={stations} 
          onBoundsChange={onBoundsChange}
          selectedFuel={selectedFuel}
          minPrice={minPrice}
          maxPrice={maxPrice}
          onStationPopupOpen={onStationPopupOpen}
          onStationDetailLinkClick={onStationDetailLinkClick}
        />
      </MapContainer>
    </div>
  );
}

export default GasStationMap;
